import { AppTableProps } from "@lib/components/MiniComponents/Table/AppTable";
import { ApiParams } from "@lib/types/Api";
import { Customer } from "@lib/types/Customer";
import { CustomerLead } from "@lib/types/CustomerLead";
import { Provider } from "@lib/types/Provider";

// Context types for different application modules
export enum AppContext {
  CUSTOMER = "whiteLabel",
  CUSTOMER_LEAD = "whiteLabelLead",
  PAYMENT_PROVIDER = "paymentProvider",
}

export interface SharedContextProps {
  context?: AppContext; // Specifies which component it is called in.
  detailContext?: AppContext; // Specifies which detail page it is called in.
  customer?: Customer;
  customerLead?: CustomerLead;
  paymentProvider?: Provider;
  customerId?: string;
  leadId?: string;
  providerId?: string;
  customTitle?: string; // Specifies the title of the page.
  customBreadcrumb?: any; // Specifies the breadcrumb of the page.
  withoutContainer?: boolean; // Specifies whether the container is hidden.
  withoutBreadcrumb?: boolean; // Specifies whether the breadcrumb is hidden.
  withoutSidebar?: boolean; // Specifies whether the sidebar is hidden.
  pageIsActive?: boolean; // Specifies whether the page is active.
  minusHeight?: number; // Specifies the height of the container.
  height?: number; // Specifies the height of the container.
  tableProps?: Partial<AppTableProps<any>>;
  queryParams?: Partial<ApiParams>;
}

// Context-specific permissions
export interface WhiteLabelPermissions {
  canViewInvoices: boolean;
  canViewTransactions: boolean;
  canViewWalletPool: boolean;
  canManageManagers: boolean;
  canViewBlockchainPayments: boolean;
}

export interface WhiteLabelLeadPermissions {
  canViewBlockchainPayments: boolean;
  canViewWalletPool: boolean;
  canManageWalletAddresses: boolean;
  canViewTransactions: boolean;
}

export interface PaymentProviderPermissions {
  canManageIntegrations: boolean;
  canViewSettings: boolean;
  canTestIntegrations: boolean;
  canManageWhiteLabelIntegrations: boolean;
  canManageWhiteLabelLeadIntegrations: boolean;
}

// Union type for all permissions
export type ContextPermissions =
  | WhiteLabelPermissions
  | WhiteLabelLeadPermissions
  | PaymentProviderPermissions;

// Context-specific data types
export interface WhiteLabelData {
  id: string;
  name: string;
  email: string;
  status: "active" | "inactive";
  createdAt: string;
  permissions: WhiteLabelPermissions;
}

export interface WhiteLabelLeadData {
  id: string;
  name: string;
  email: string;
  status: "active" | "inactive";
  createdAt: string;
  whiteLabelCustomerId: string;
  permissions: WhiteLabelLeadPermissions;
}

export interface PaymentProviderData {
  id: string;
  name: string;
  type: "card" | "bank" | "crypto" | "other";
  status: "active" | "inactive" | "pending";
  createdAt: string;
  config: {
    apiKey?: string;
    secretKey?: string;
    webhookUrl?: string;
    supportedCurrencies: string[];
    supportedPaymentMethods: string[];
  };
  integrations: {
    whiteLabelCustomers: string[];
    whiteLabelLeads: string[];
  };
  permissions: PaymentProviderPermissions;
}

// Union type for all context data
export type ContextData =
  | WhiteLabelData
  | WhiteLabelLeadData
  | PaymentProviderData;

// Helper type to get context-specific data type
export type ContextDataType<T extends AppContext> =
  T extends AppContext.CUSTOMER
    ? WhiteLabelData
    : T extends AppContext.CUSTOMER_LEAD
      ? WhiteLabelLeadData
      : T extends AppContext.PAYMENT_PROVIDER
        ? PaymentProviderData
        : never;

// Helper type to get context-specific permissions type
export type ContextPermissionsType<T extends AppContext> =
  T extends AppContext.CUSTOMER
    ? WhiteLabelPermissions
    : T extends AppContext.CUSTOMER_LEAD
      ? WhiteLabelLeadPermissions
      : T extends AppContext.PAYMENT_PROVIDER
        ? PaymentProviderPermissions
        : never;
